import React, { useState, useEffect } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import Select from 'react-select';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources, scopes } from "../authConfig";
import masterDataConfig from '../settings/masterData.json';
import ForeignTableSelect from '../components/utils/ForeignTableSelect';
import UserSelect from '../components/utils/UserSelect';

function MasterDataEditForm({ type, data, onDone }) {
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    const [columns, setColumns] = useState([]);
    const [formData, setFormData] = useState(data);
    const [isUpdate, setIsUpdate] = useState(false);
    const [relatedFields, setRelatedFields] = useState({});

    useEffect(() => {
        if (data.id) {
            setIsUpdate(true);
        } else {
            setIsUpdate(false);
        }
        setColumns(masterDataConfig.columns);
        if (type) {
            const master = masterDataConfig.masters.find(m => m.type === type);
            if (master) {
                setColumns(master.columns || []);
                
                // 連関フィールドの設定を初期化
                const relatedFieldsMap = {};
                master.columns.forEach(column => {
                    if (column.relatedFields) {
                        relatedFieldsMap[column.name] = column.relatedFields;
                    }
                });
                setRelatedFields(relatedFieldsMap);
            } else {
                setColumns([]);
            }
        }
    }, [data, type]);

    const handleChange = (event) => {
        let value;
        if (event.target.type === 'checkbox') {
            value = event.target.checked;
        } else if ( event.target.type === 'select-multiple') {
            value = Array.from(event.target.selectedOptions).map(option => option.value);
        } else if (event.target.type === 'select-one' ) {
            value = event.target.selectedOptions[0].value;
        } else {
            value = event.target.value;
        }

        const fieldName = event.target.name;
        setFormData({
            ...formData,
            [fieldName]: value,
        });
    };

    const handleSelectChange = (selectedOption, columnName) => {
        setFormData({
            ...formData,
            [columnName]: selectedOption ? selectedOption.value : null,
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        console.log("call handleSubmit");

        // 作成日と更新日を設定
        const currentDate = new Date().toISOString();
        const updatedFormData = {
            ...formData,
            updatedDate: currentDate
        };
        try {
            if (!formData.id) {
                updatedFormData.createdDate = currentDate;
            }
            let response;
            if (isUpdate === false) {
                console.log("call create");
                response = await execute("POST", protectedResources.createMasterData.endpoint + "/" + type, updatedFormData);
            } else {
                console.log("call update");
                response = await execute("PUT", protectedResources.updateMasterData.endpoint + "/" + type, updatedFormData);
            }
            console.log("Response:", response);
            onDone();
        } catch (error) {
            console.error("Error fetching master data:", error);
        }
    };

    // カラムが連関フィールドを持つか確認する関数
    const getParentFieldName = (columnName) => {
        for (const [parentField, relatedFieldsList] of Object.entries(relatedFields)) {
            for (const field of relatedFieldsList) {
                if (field.field === columnName) {
                    return parentField;
                }
            }
        }
        return null;
    };

    // 連関フィルター情報を取得する関数
    const getRelatedFieldInfo = (columnName) => {
        for (const [parentField, relatedFieldsList] of Object.entries(relatedFields)) {
            for (const field of relatedFieldsList) {
                if (field.field === columnName) {
                    return {
                        parentField,
                        foreignKeyField: field.foreignKeyField,
                        foreignTable: field.foreignTable
                    };
                }
            }
        }
        return null;
    };

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    <div className="row g-3">
                        {columns.map(column => (
                            !column.hidden ? (
                                <div className="col-md-6" key={column.name}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-medium">{column.displayName}</Form.Label>
                                        {column.isPrimaryKey && (
                                            <Form.Control 
                                                type={column.type} 
                                                name={column.name} 
                                                value={formData[column.name]} 
                                                onChange={handleChange} 
                                                disabled 
                                                className="bg-light"
                                            />
                                        )}
                                        {!column.isPrimaryKey && !column.isForeignKey && column.type !== 'dropdown' && (
                                            <Form.Control 
                                                type={column.type} 
                                                name={column.name} 
                                                value={formData[column.name]} 
                                                onChange={handleChange} 
                                                className="border-secondary-subtle"
                                            />
                                        )}
                                        {!column.isPrimaryKey && !column.isForeignKey && column.type === 'dropdown' && (
                                            <Select
                                                name={column.name}
                                                value={column.values 
                                                    ? column.values
                                                        .filter(option => option.value === formData[column.name])
                                                        .map(option => ({
                                                            value: option.value,
                                                            label: option.displayName
                                                        }))[0] 
                                                    : null
                                                }
                                                onChange={(selectedOption) => handleSelectChange(selectedOption, column.name)}
                                                options={column.values ? column.values.map(option => ({
                                                    value: option.value,
                                                    label: option.displayName
                                                })) : []}
                                                placeholder="選択してください"
                                                isClearable
                                                className="basic-select"
                                                classNamePrefix="select"
                                                noOptionsMessage={() => "該当する選択肢がありません"}
                                            />
                                        )}
                                        {!column.isPrimaryKey && column.isForeignKey && column.name !== "userId" && (
                                            <ForeignTableSelect 
                                                type={column.foreignTable} 
                                                displayName={column.displayName} 
                                                value={formData[column.name] || ''} 
                                                onChange={handleChange}
                                                name={column.name}
                                                // 連関フィールドの関連情報を追加
                                                relatedInfo={getRelatedFieldInfo(column.name)}
                                                parentValue={getParentFieldName(column.name) ? formData[getParentFieldName(column.name)] : null}
                                            />
                                        )}
                                        {!column.isPrimaryKey && column.isForeignKey && column.name === "userId" && (
                                            <UserSelect 
                                                type="user" 
                                                displayName={column.displayName} 
                                                value={formData[column.name]} 
                                                onChange={handleChange} 
                                            />    
                                        )}
                                    </Form.Group>
                                </div>
                            ) : (
                                <Form.Group key={column.name}>
                                    <Form.Control type="hidden" name={column.name} value={formData[column.name]} />
                                </Form.Group>
                            )
                        ))}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button variant="outline-secondary" onClick={onDone}>
                            キャンセル
                        </Button>
                        <Button variant="primary" type="submit" className="px-4">
                            保存
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
}

export default MasterDataEditForm;